import type { JsonValue, RecordDefinition, Resource, Snapshot } from "@e308/core";
import { hasBlastDoors } from "./combat.js";
import type { CombatState } from "./combat-types.js";
import type { EconomySettings, SeatModel } from "./content-types.js";
import { ECONOMY_BUILDINGS } from "./economy-types.js";
import { formatMicro, type Micro } from "./exact-numbers.js";
import { borderIsActive } from "./lifecycle-state.js";
import type { MatchLifecycleState } from "./lifecycle-types.js";
import { supplyForPosition } from "./logistics-state.js";
import type { BorderPosition, LogisticsState } from "./logistics-types.js";
import type { ManufacturingState } from "./manufacturing-types.js";
import type { ResearchState } from "./research-types.js";
import { observeEconomy } from "./simulation.js";
import type { SeatState } from "./types.js";

export function observeSeat(
  snapshot: Snapshot<Micro>,
  model: SeatModel,
  lifecycleDefinition: RecordDefinition<MatchLifecycleState & JsonValue>,
): SeatState {
  const get = (resource: Resource<Micro>) => snapshot.resources[resource.id] as Micro;
  const settings = snapshot.records?.[model.economySettings.id]
    ?.value as unknown as EconomySettings;
  const economy = observeEconomy(snapshot, model);
  const allocation = (id: string, target: string) => {
    const values = snapshot.allocations[id] as Readonly<Record<string, Micro>>;
    return Number((values[target] as Micro) / 1_000_000n);
  };
  return Object.freeze({
    id: model.seat.id,
    name: model.seat.name,
    alive: get(model.resources.headquarters) > 0n,
    headquartersIntegrity: formatMicro(get(model.resources.headquarters)),
    metal: formatMicro(get(model.resources.metal)),
    researchPoints: formatMicro(get(model.resources.researchPoints)),
    guardRobots: formatMicro(get(model.resources.guardRobot)),
    units: Object.freeze({
      "guard-robot": formatMicro(get(model.resources.guardRobot)),
      "raider-robot": formatMicro(get(model.resources.raiderRobot)),
      "siege-gun": formatMicro(get(model.resources.siegeGun)),
    }),
    buildings: economy.buildings,
    wreckage: Object.freeze(
      Object.fromEntries(
        ECONOMY_BUILDINGS.map((id) => [id, formatMicro(get(model.resources.wreckage[id]))]),
      ),
    ) as SeatState["wreckage"],
    manufacturing: snapshot.records?.[model.manufacturing.id]
      ?.value as unknown as ManufacturingState,
    research: snapshot.records?.[model.research.id]?.value as unknown as ResearchState,
    constructionSplit: Object.freeze({
      newConstruction: allocation(model.constructionSplit.id, "new-construction"),
      restoration: allocation(model.constructionSplit.id, "restoration"),
    }),
    powerOperation: Object.freeze({
      industry: allocation(model.powerOperation.id, "industry"),
      defenses: allocation(model.powerOperation.id, "defenses"),
      militaryProduction: allocation(model.powerOperation.id, "military-production"),
      research: allocation(model.powerOperation.id, "research"),
    }),
    powerPriority: settings.powerPriority,
    metalPriority: settings.metalPriority,
    factoryWorksSplit: Object.freeze({
      "rig-factory": allocation(model.factoryWorksSplit.id, "rig-factory"),
      "yard-factory": allocation(model.factoryWorksSplit.id, "yard-factory"),
      "robot-factory": allocation(model.factoryWorksSplit.id, "robot-factory"),
    }),
    economy: economy.projection,
    blastDoorsActive: hasBlastDoors(economy.projection.operatedShieldGenerators),
    ...observeBorders(snapshot, model, lifecycleDefinition, economy.projection.supplyCapacity),
  });
}

function observeBorders(
  snapshot: Snapshot<Micro>,
  model: SeatModel,
  lifecycleDefinition: RecordDefinition<MatchLifecycleState & JsonValue>,
  supplyCapacity: string,
): Pick<
  SeatState,
  "logistics" | "combat" | "targeting" | "supplyAllocation" | "shieldAllocation" | "borderSupply"
> {
  const logistics = snapshot.records?.[model.logistics.id]?.value as unknown as LogisticsState;
  const combat = snapshot.records?.[model.combat.id]?.value as unknown as CombatState;
  const lifecycle = snapshot.records?.[lifecycleDefinition.id]
    ?.value as unknown as MatchLifecycleState;
  const active = model.borderIds.filter((id) => borderIsActive(lifecycle, model.seat.id, id));
  const allocation = (id: string, target: string) => {
    const values = snapshot.allocations[id] as Readonly<Record<string, Micro>>;
    return Number((values[target] as Micro) / 1_000_000n);
  };
  const allocations = (id: string) =>
    Object.freeze(Object.fromEntries(active.map((target) => [target, allocation(id, target)])));
  return {
    logistics: Object.freeze({
      ...logistics,
      borders: Object.freeze(
        Object.fromEntries(active.map((id) => [id, logistics.borders[id] as BorderPosition])),
      ),
    }),
    combat: Object.freeze({
      borders: Object.freeze(
        Object.fromEntries(
          active.map((id) => [id, combat.borders[id] as CombatState["borders"][string]]),
        ),
      ),
    }),
    targeting: allocations(model.targeting.id),
    supplyAllocation: allocations(model.supplyAllocation.id),
    shieldAllocation: allocations(model.shieldAllocation.id),
    borderSupply: Object.freeze(
      Object.fromEntries(
        active.map((id) => [
          id,
          supplyForPosition(
            (logistics.borders[id] as BorderPosition).troops,
            supplyCapacity,
            allocation(model.supplyAllocation.id, id),
          ),
        ]),
      ),
    ),
  };
}
